import React, { useCallback, useEffect, useState } from 'react';
import './BankMarketingEdaPage.css';

const BankMarketingEdaPage: React.FC = () => {
  const [sections, setSections] = useState<JSX.Element[]>([]);

  // Helper function to recursively process HTML nodes and their children
  const processNode = useCallback(
    (node: Node, keyPrefix: string, index: number): JSX.Element | null => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent ? <>{node.textContent}</> : null;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return null;
      }

      const element = node as HTMLElement;
      const key = `${keyPrefix}-${index}`;
      const childElements = Array.from(element.childNodes)
        .map((child, childIndex) => processNode(child, key, childIndex))
        .filter(Boolean) as JSX.Element[];

      switch (element.nodeName) {
        case 'H1':
          return <h1 key={key}>{childElements}</h1>;
        case 'H2':
          return (
            <h2 key={key} id={element.id}>
              {childElements}
            </h2>
          );
        case 'H3':
          return (
            <h3 key={key} id={element.id}>
              {childElements}
            </h3>
          );
        case 'H4':
          return (
            <h4 key={key} id={element.id}>
              {childElements}
            </h4>
          );
        case 'P':
          return <p key={key}>{childElements}</p>;
        case 'A':
          return (
            <a
              key={key}
              href={element.getAttribute('href') || '#'}
              target={element.getAttribute('href')?.startsWith('http') ? '_blank' : undefined}
              rel={
                element.getAttribute('href')?.startsWith('http') ? 'noopener noreferrer' : undefined
              }
            >
              {childElements}
            </a>
          );
        case 'IMG':
          const imgSrc = element.getAttribute('src') || '';
          // Fix relative image paths
          const fixedSrc = imgSrc.startsWith('/') ? imgSrc : `/${imgSrc}`;
          return (
            <img
              key={key}
              src={fixedSrc}
              alt={element.getAttribute('alt') || 'Bank marketing analysis image'}
            />
          );
        case 'UL':
          return <ul key={key}>{childElements}</ul>;
        case 'OL':
          return <ol key={key}>{childElements}</ol>;
        case 'LI':
          return <li key={key}>{childElements}</li>;
        case 'TABLE':
          return <table key={key}>{childElements}</table>;
        case 'THEAD':
          return <thead key={key}>{childElements}</thead>;
        case 'TBODY':
          return <tbody key={key}>{childElements}</tbody>;
        case 'TR':
          return <tr key={key}>{childElements}</tr>;
        case 'TH':
          return <th key={key}>{childElements}</th>;
        case 'TD':
          return <td key={key}>{childElements}</td>;
        case 'PRE':
          return <pre key={key}>{childElements}</pre>;
        case 'CODE':
          return <code key={key}>{childElements}</code>;
        case 'BLOCKQUOTE':
          return <blockquote key={key}>{childElements}</blockquote>;
        default:
          return <>{childElements}</>;
      }
    },
    []
  );

  useEffect(() => {
    fetch('/bank_marketing.html')
      .then(response => response.text())
      .then(html => {
        // Extract the body content from the HTML
        const bodyContent = html.match(/<body>([\s\S]*)<\/body>/i)?.[1] || '';

        // Parse the HTML content
        const parser = new DOMParser();
        const doc = parser.parseFromString(bodyContent, 'text/html');

        // Convert the HTML elements to React elements using our recursive function
        const elements = Array.from(doc.body.children)
          .map((node, index) => processNode(node, 'root', index))
          .filter(Boolean) as JSX.Element[];

        setSections(elements);
      })
      .catch(err => console.error('Error fetching HTML:', err));
  }, [processNode]);

  return <div className="bank-marketing-eda-content">{sections}</div>;
};

export default BankMarketingEdaPage;
